import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import moment from 'moment';
import { toast } from 'react-toastify';
import axios from 'helpers/api';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import { Navigate } from 'react-router-dom';
import paths from 'routes/paths';

/**
 * Standalone page: bulk-import day program patients from a CSV file.
 *
 * Self-contained — carries its own day-program picker (from the localStorage
 * `dayProgramOptions` written at login, shape { key: name, value: id }) and
 * depends on no other day-program component. Reachable directly at
 * paths.importDayProgramPatients.
 *
 * The backend endpoint POST /day-programs-import-patients/bulk-create-patients takes a JSON
 * `patients` array (not a file upload), so the CSV is parsed and validated in
 * the browser; only the JSON payload crosses the wire. Rows are previewed with
 * per-row validation before anything is sent, and the server's per-row failures
 * are surfaced back into the same table after upload.
 */

// Header aliases → canonical backend field. Import is forgiving about column
// naming/casing so a hand-exported spreadsheet still lines up.
const FIELD_ALIASES = {
  first_name: 'first_name', firstname: 'first_name', 'first name': 'first_name',
  middle_name: 'middle_name', middlename: 'middle_name', 'middle name': 'middle_name',
  last_name: 'last_name', lastname: 'last_name', 'last name': 'last_name',
  dob: 'dob', date_of_birth: 'dob', 'date of birth': 'dob', birthdate: 'dob',
  gender: 'gender', sex: 'gender',
  address: 'address', address1: 'address', 'address 1': 'address',
  address2: 'address2', 'address 2': 'address2',
  city: 'city', state: 'state', zip: 'zip', zipcode: 'zip', 'zip code': 'zip',
  phone: 'phone', fax: 'fax',
  primary_language: 'primary_language', language: 'primary_language',
  ethnicity: 'ethnicity', allergies: 'allergies',
  medsheet_note: 'medsheet_note', note: 'medsheet_note', notes: 'medsheet_note',
  admit_date: 'admit_date', 'admit date': 'admit_date',
  physical_exam_date: 'physical_exam_date', 'physical exam date': 'physical_exam_date',
  insurance_info: 'insurance_info', insurance_group: 'insurance_group',
  insurance_no: 'insurance_no', insurance_priority: 'insurance_priority',
  physician_id: 'physician_id', dds_office_id: 'dds_office_id', protect_self: 'protect_self'
};

// Static sample CSV shipped in public/samples (served at PUBLIC_URL).
const SAMPLE_CSV_URL = `${process.env.PUBLIC_URL || ''}/samples/day_program_patients_sample.csv`;

const REQUIRED_FIELDS = ['first_name', 'last_name', 'dob', 'gender'];

// AddPatient sends gender as 1/2/3; accept friendly spellings from a CSV too.
const GENDER_MAP = {
  '1': '1', male: '1', m: '1',
  '2': '2', female: '2', f: '2',
  '3': '3', other: '3', others: '3', o: '3'
};

const normalizeGender = raw => GENDER_MAP[String(raw || '').trim().toLowerCase()] || null;

// Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped quotes ("")
// and commas/newlines inside quotes. Kept local so the import has no new dep
// (papaparse is not in the bundle) — medical rows carry commas in addresses,
// so a naive split would corrupt them.
const parseCsv = text => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const src = String(text || '').replace(/^﻿/, ''); // strip BOM

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\r') {
      // swallow; \n handles the row break
    } else if (ch === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else {
      field += ch;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter(r => r.some(c => String(c).trim() !== ''));
};

const validateRow = mapped => {
  const errors = [];
  REQUIRED_FIELDS.forEach(f => {
    if (f === 'gender' || f === 'dob') return; // format-checked below
    if (!mapped[f] || !String(mapped[f]).trim()) {
      errors.push(`Missing ${f.replace('_', ' ')}`);
    }
  });

  if (!mapped.dob || !String(mapped.dob).trim()) {
    errors.push('Missing dob');
  } else if (!moment(mapped.dob, 'MM/DD/YYYY', true).isValid()) {
    errors.push('dob must be MM/DD/YYYY');
  }

  if (!mapped.gender || !String(mapped.gender).trim()) {
    errors.push('Missing gender');
  } else if (!normalizeGender(mapped.gender)) {
    errors.push('gender must be Male/Female/Other');
  }

  ['admit_date', 'physical_exam_date'].forEach(f => {
    if (mapped[f] && String(mapped[f]).trim() && !moment(mapped[f], 'MM/DD/YYYY', true).isValid()) {
      errors.push(`${f.replace('_', ' ')} must be MM/DD/YYYY`);
    }
  });

  return errors;
};

// This page is nav-gated to pharmacy admins (role 1/2), who have no single
// group — so, like /admin/add-facility-staff, they pick a Group first and the
// Day Program list is scoped to it. groupsData (built at login) carries
// { key, value, access_control } per group; access_control value 4 =
// is_dayprogram_active (same mapping AddFacilityStaff uses).
const DAYPROGRAM_ACCESS_FLAG = 4;

const getGroupOptions = () => {
  try {
    const opts = JSON.parse(localStorage.getItem('groupsData') || '[]');
    return Array.isArray(opts) ? opts : [];
  } catch (error) {
    return [];
  }
};

const groupHasDayProgram = group =>
  Array.isArray(group?.access_control) &&
  group.access_control.map(Number).includes(DAYPROGRAM_ACCESS_FLAG);

// /day-programs/list returns programs across groups for pharmacy admins; scope
// to the picked group and keep only active ones. Mirrors AddFacilityStaff's
// normalizeDayProgramOptions.
const toDayProgramOptions = (programs, groupId) =>
  (Array.isArray(programs) ? programs : [])
    .filter(p => Number(p?.is_active) === 1)
    .filter(p => Number(p?.group_id) === Number(groupId))
    .map(p => ({ key: p.name || p.day_program_name, value: p.id || p.day_program_id }));

const ImportDayProgramPatients = () => {
  const currentRoleId = Number(localStorage.getItem("roleId"));
  if (currentRoleId >= 3) {
    return <Navigate to={paths.accessDenied} replace />;
  }

  const groupOptions = useMemo(getGroupOptions, []);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [dayProgramOptions, setDayProgramOptions] = useState([]);
  const [dayProgramsLoading, setDayProgramsLoading] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]); // [{ mapped, errors, serverErrors }]
  const [unknownColumns, setUnknownColumns] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const dayProgramId = selectedProgram?.value;
  const dayProgramName = selectedProgram?.key || '';
  const dayProgramEnabled = selectedGroup ? groupHasDayProgram(selectedGroup) : false;

  // Load the picked group's day programs. Runs only when the group is
  // day-program-enabled; the endpoint is available to pharmacy admins (role<=3).
  useEffect(() => {
    let cancelled = false;
    // Reset any downstream selection/state when the group changes.
    setSelectedProgram(null);
    setDayProgramOptions([]);
    setRows([]); setFileName(''); setParseError(''); setResult(null); setUnknownColumns([]);

    if (!selectedGroup || !groupHasDayProgram(selectedGroup)) return;

    (async () => {
      setDayProgramsLoading(true);
      try {
        const { data } = await axios.get('/day-programs/list');
        const programs = data?.data?.day_programs || data?.day_programs || data?.data || data || [];
        if (!cancelled) setDayProgramOptions(toDayProgramOptions(programs, selectedGroup.value));
      } catch (error) {
        if (!cancelled) {
          setDayProgramOptions([]);
          toast.error('Unable to load day programs for this group.');
        }
      } finally {
        if (!cancelled) setDayProgramsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedGroup]);

  const clearFile = () => {
    setFileName(''); setRows([]); setUnknownColumns([]);
    setParseError(''); setResult(null);
  };

  const parseFile = useCallback(file => {
    setParseError(''); setResult(null); setRows([]); setUnknownColumns([]);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const matrix = parseCsv(String(e.target.result || ''));
        if (matrix.length < 2) {
          setParseError('The CSV needs a header row and at least one patient row.');
          return;
        }

        const headers = matrix[0].map(h => String(h).trim());
        const canonical = headers.map(h => FIELD_ALIASES[h.toLowerCase()] || null);
        const unknown = headers.filter((h, i) => h && !canonical[i]);
        setUnknownColumns([...new Set(unknown)]);

        const parsed = matrix.slice(1).map(cells => {
          const mapped = {};
          canonical.forEach((field, i) => {
            if (field) mapped[field] = (cells[i] !== undefined ? String(cells[i]).trim() : '');
          });
          return { mapped, errors: validateRow(mapped), serverErrors: [] };
        });

        setRows(parsed);
      } catch (err) {
        setParseError('Could not read this file. Please upload a valid .csv.');
      }
    };
    reader.onerror = () => setParseError('Could not read this file.');
    reader.readAsText(file);
  }, []);

  const onDrop = useCallback(accepted => {
    if (accepted && accepted[0]) parseFile(accepted[0]);
  }, [parseFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] },
    multiple: false,
    disabled: submitting
  });

  const validRows = useMemo(() => rows.filter(r => r.errors.length === 0), [rows]);
  const invalidCount = rows.length - validRows.length;

  const handleUpload = async () => {
    if (!dayProgramId) {
      toast.error('Select a day program first.');
      return;
    }
    if (validRows.length === 0) {
      toast.error('There are no valid rows to import.');
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const patients = validRows.map(r => ({
        ...r.mapped,
        gender: normalizeGender(r.mapped.gender)
      }));

      const { data } = await axios.post('/day-programs-import-patients/bulk-create-patients', {
        day_program_id: dayProgramId,
        patients
      });

      // Map server-side per-row failures back onto the previewed valid rows.
      // The server indexes `failed` against the array we sent (validRows order).
      if (Array.isArray(data?.failed) && data.failed.length > 0) {
        setRows(prev => {
          const next = prev.map(r => ({ ...r }));
          const validRefs = next.filter(r => r.errors.length === 0);
          data.failed.forEach(f => {
            const ref = validRefs[f.index];
            if (ref) ref.serverErrors = f.errors || ['Server rejected this row'];
          });
          return next;
        });
      }

      setResult({
        created: data?.created_count || 0,
        enrolled: data?.enrolled_count || 0,
        duplicates: data?.duplicate_count || 0,
        failed: Array.isArray(data?.failed) ? data.failed.length : 0
      });

      if ((data?.created_count || 0) > 0) {
        toast.success(`${data.created_count} patient(s) imported into ${dayProgramName || 'the day program'}.`);
      } else if ((data?.duplicate_count || 0) > 0) {
        toast.info(`All ${data.duplicate_count} patient(s) already existed — none created.`);
      } else {
        toast.warning(data?.message || 'No patients were created.');
      }
    } catch (error) {
      const msg = error?.response?.data?.message || 'Import failed. Please try again.';
      // Whole-batch validation rejection (400) returns { failed: [{index, errors}] }.
      const failed = error?.response?.data?.failed;
      if (Array.isArray(failed) && failed.length > 0) {
        setRows(prev => {
          const next = prev.map(r => ({ ...r }));
          const validRefs = next.filter(r => r.errors.length === 0);
          failed.forEach(f => {
            const ref = validRefs[f.index] || next[f.index];
            if (ref) ref.serverErrors = f.errors || [msg];
          });
          return next;
        });
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const rowStatus = r => {
    if (r.serverErrors && r.serverErrors.length > 0) return { variant: 'danger', label: 'Failed' };
    if (r.errors.length > 0) return { variant: 'warning', label: 'Skipped' };
    if (result) return { variant: 'success', label: 'Imported' };
    return { variant: 'info', label: 'Ready' };
  };

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'Day Program Patients', href: paths.viewDayProgramPatients, isActive: false },
    { name: 'Import Patients (CSV)', href: paths.importDayProgramPatients, isActive: true }
  ];

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
      <Card>
        <Card.Header className="pb-0">
          <h5 className="mb-0">Import Day Program Patients (CSV)</h5>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-end mb-3">
            <Col xs="12" md="4" className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Label>Group</Form.Label>
                <ThemedSelect
                  name="group"
                  closeMenuOnSelect
                  placeholder="Select Group"
                  classNamePrefix="react-select"
                  options={groupOptions}
                  value={selectedGroup}
                  getOptionLabel={option => option.key}
                  getOptionValue={option => option.value}
                  onChange={setSelectedGroup}
                />
              </Form.Group>
            </Col>
            <Col xs="12" md="4" className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Label>Day Program</Form.Label>
                <ThemedSelect
                  name="dayProgram"
                  closeMenuOnSelect
                  placeholder={dayProgramsLoading ? 'Loading…' : 'Select Day Program'}
                  classNamePrefix="react-select"
                  options={dayProgramOptions}
                  value={selectedProgram}
                  isDisabled={!dayProgramEnabled || dayProgramsLoading || dayProgramOptions.length === 0}
                  getOptionLabel={option => option.key}
                  getOptionValue={option => option.value}
                  onChange={setSelectedProgram}
                />
              </Form.Group>
            </Col>
            <Col xs="12" md="4" className="text-md-end">
              <a
                className="btn btn-link btn-sm p-0"
                href={SAMPLE_CSV_URL}
                download="day_program_patients_sample.csv"
              >
                Download sample CSV
              </a>
            </Col>
          </Row>

          {groupOptions.length === 0 && (
            <Alert variant="warning">No groups are available on this account.</Alert>
          )}

          {selectedGroup && !dayProgramEnabled && (
            <Alert variant="warning">Day program is not enabled for this group.</Alert>
          )}

          {selectedGroup && dayProgramEnabled && !dayProgramsLoading && dayProgramOptions.length === 0 && (
            <Alert variant="warning">No day program found for this group — nothing to import into.</Alert>
          )}

          <div className="fs-10 text-600 mb-2">
            Required columns: <strong>first_name, last_name, dob (MM/DD/YYYY), gender</strong>.
            Gender accepts Male/Female/Other (or 1/2/3).
            Allergies are optional and comma-separated (e.g. "Peanuts, Latex").
            Names that match this group's configured allergies are linked; any new
            name is added to the allergy list automatically.
          </div>

          <div
            {...getRootProps({
              className: `dropzone-area px-2 py-4 text-center border rounded ${isDragActive ? 'border-primary bg-light' : ''}`
            })}
            style={{ cursor: submitting ? 'not-allowed' : 'pointer' }}
          >
            <input {...getInputProps()} />
            <p className="mb-1 fs-9">
              {isDragActive ? 'Drop the CSV here…' : 'Drag & drop a CSV here, or click to browse'}
            </p>
            {fileName && (
              <p className="mb-0 fs-10 text-500">
                Selected: {fileName}
                <Button variant="link" size="sm" className="p-0 ms-2" onClick={clearFile}>clear</Button>
              </p>
            )}
          </div>

          {parseError && <Alert variant="danger" className="mt-3 mb-0">{parseError}</Alert>}

          {unknownColumns.length > 0 && (
            <Alert variant="info" className="mt-3 mb-0 fs-10">
              Ignored unrecognized column(s): {unknownColumns.join(', ')}
            </Alert>
          )}

          {rows.length > 0 && (
            <>
              <div className="d-flex gap-3 mt-3 mb-2 fs-10">
                <span>Total rows: <strong>{rows.length}</strong></span>
                <span className="text-success">Valid: <strong>{validRows.length}</strong></span>
                {invalidCount > 0 && <span className="text-warning">Invalid: <strong>{invalidCount}</strong></span>}
              </div>

              <div style={{ maxHeight: 360, overflowY: 'auto' }} className="border rounded">
                <Table size="sm" striped className="fs-10 mb-0">
                  <thead className="bg-200 position-sticky top-0">
                    <tr>
                      <th>#</th>
                      <th>Status</th>
                      <th>First</th>
                      <th>Last</th>
                      <th>DOB</th>
                      <th>Gender</th>
                      <th>Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const s = rowStatus(r);
                      const issues = [...(r.errors || []), ...(r.serverErrors || [])];
                      return (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td><Badge bg={s.variant}>{s.label}</Badge></td>
                          <td>{r.mapped.first_name}</td>
                          <td>{r.mapped.last_name}</td>
                          <td>{r.mapped.dob}</td>
                          <td>{r.mapped.gender}</td>
                          <td className="text-danger">{issues.join('; ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {result && (
            <Alert variant={result.created > 0 ? 'success' : 'warning'} className="mt-3 mb-0">
              Imported <strong>{result.created}</strong> patient(s),
              enrolled <strong>{result.enrolled}</strong>
              {result.duplicates > 0 && <>, <strong>{result.duplicates}</strong> already existed (skipped)</>}
              {result.failed > 0 && <>, <strong>{result.failed}</strong> failed (see table)</>}.
            </Alert>
          )}
        </Card.Body>
        <Card.Footer className="text-end">
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={submitting || !dayProgramId || validRows.length === 0}
          >
            {submitting ? 'Importing…' : `Import ${validRows.length || ''} Patient(s)`}
          </Button>
        </Card.Footer>
      </Card>
    </>
  );
};

export default ImportDayProgramPatients;
